/**
 * The list page's column-header filters — lifted out of `ActionItemsList.tsx`
 * (ADR-0221 Phase 0, E4) so the Issues pages copy the action-item list
 * without copying its components. Behaviour unchanged.
 *
 *   - `HeaderCell` — a sortable `<th>` with an optional funnel that opens a
 *     `ColumnFilterPopover` holding whatever filter content it is given.
 *   - `TextFilterPopover` — a "contains" text filter, applied on Enter / Apply.
 *   - `MultiSelectPopover` — a checkbox list over `{ id, label }` options.
 *   - `FilterChip` — one active filter in the `filterSummaryStrip`.
 */

import { useEffect, useRef, useState } from "react";
import { ColumnFilterPopover, IconFunnel } from "./ColumnFilterPopover.js";

export function HeaderCell<S extends string>(props: {
  title: string;
  sortKey?: S;
  currentSort?: S | null;
  currentOrder?: "asc" | "desc";
  onToggleSort?: (col: S) => void;
  isFilterActive?: boolean;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const sorted = !!props.sortKey && props.currentSort === props.sortKey;
  // Only render a sort arrow on the actively-sorted column. Unsorted columns
  // have no placeholder — saves horizontal space across 9 columns.
  const arrow = sorted ? (props.currentOrder === "asc" ? "↑" : "↓") : null;

  return (
    <th>
      <span className="thHeaderBar">
        {props.sortKey && props.onToggleSort ? (
          <button
            type="button"
            className={`thSortable${sorted ? " isSorted" : ""}`}
            onClick={() => props.onToggleSort!(props.sortKey!)}
            title={`Sort by ${props.title}`}
          >
            {props.title}
            {arrow ? <span className="sortArrow">{arrow}</span> : null}
          </button>
        ) : (
          <span style={{ fontWeight: 700 }}>{props.title}</span>
        )}
        {props.children ? (
          <button
            ref={anchorRef}
            type="button"
            className={`thFilter${props.isFilterActive ? " isActive" : ""}`}
            onClick={() => setOpen((v) => !v)}
            title={`Filter ${props.title}`}
            aria-expanded={open}
          >
            <IconFunnel size={12} filled={!!props.isFilterActive} />
          </button>
        ) : null}
      </span>
      {open && props.children ? (
        <ColumnFilterPopover anchor={anchorRef.current} onClose={() => setOpen(false)}>
          {props.children}
        </ColumnFilterPopover>
      ) : null}
    </th>
  );
}

export function TextFilterPopover(props: { value: string; onApply: (v: string) => void }) {
  const [draft, setDraft] = useState(props.value);
  useEffect(() => setDraft(props.value), [props.value]);
  return (
    <>
      <input
        autoFocus
        className="input"
        placeholder="Contains…"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") props.onApply(draft);
        }}
      />
      <div className="colFilterActions">
        <button type="button" className="btn" onClick={() => props.onApply("")}>Clear</button>
        <button type="button" className="btn btnPrimary" onClick={() => props.onApply(draft)}>Apply</button>
      </div>
    </>
  );
}

export function MultiSelectPopover(props: {
  title: string;
  options: Array<{ id: string; label: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
  emptyMessage?: string;
}) {
  const selectedSet = new Set(props.selected);
  function toggle(id: string) {
    const next = selectedSet.has(id)
      ? props.selected.filter((s) => s !== id)
      : [...props.selected, id];
    props.onChange(next);
  }
  return (
    <>
      <div className="colFilterHeader">{props.title}</div>
      {props.options.length === 0 ? (
        <div className="muted" style={{ fontSize: 12 }}>{props.emptyMessage ?? "No options."}</div>
      ) : (
        <div style={{ display: "grid", gap: 2, maxHeight: 260, overflowY: "auto" }}>
          {props.options.map((opt) => (
            <label key={opt.id} className="colFilterCheckbox">
              <input type="checkbox" checked={selectedSet.has(opt.id)} onChange={() => toggle(opt.id)} />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      )}
      <div className="colFilterActions">
        <button
          type="button"
          className="btn"
          onClick={() => props.onChange([])}
          disabled={props.selected.length === 0}
        >
          Clear
        </button>
      </div>
    </>
  );
}

export function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="filterSummaryChip">
      <span>{label}</span>
      <button type="button" aria-label="Clear this filter" onClick={onClear}>×</button>
    </span>
  );
}
